import { useEffect, useRef } from 'react';
import { useAlert } from '../contexts/AlertContext';
import { config, TradingPlatforms } from '@/config';

interface MarketHoursConfig {
    enabled?: boolean;
    weekendMessage?: string;
    marketCloseMessage?: string;
}

export const useMarketHoursAlert = ({
    enabled = true,
    weekendMessage = config.platform === TradingPlatforms.TopstepIB ? "We are performing scheduled maintenance and some features may be temporarily unavailable. Account balances may not reflect updates and should not be relied upon until maintenance is complete. Thank you for your patience." : "We are undergoing scheduled maintenance, which may temporarily affect some functionalities. We appreciate your patience.",
    marketCloseMessage = config.platform === TradingPlatforms.TopstepIB ? "We are performing scheduled maintenance and some features may be temporarily unavailable. Account balances may not reflect updates and should not be relied upon until maintenance is complete. Thank you for your patience." : "We are undergoing scheduled maintenance, which may temporarily affect some functionalities. We appreciate your patience."
}: MarketHoursConfig) => {



    const { showAlert, hideAlert } = useAlert();
    const alertIdRef = useRef<string | number | null>(null);

    useEffect(() => {
        if (!enabled) {
            if (alertIdRef.current) {
                hideAlert(alertIdRef.current);
                alertIdRef.current = null;
            }
            return;
        }

        const checkMarketStatus = () => {
            const now = new Date();
            const ctTime = new Date(now.toLocaleString("en-US", { timeZone: "America/Chicago" }));

            const day = ctTime.getDay();
            const hour = ctTime.getHours();
            const minutes = ctTime.getMinutes();
            const time = hour * 100 + minutes;

            if (alertIdRef.current) {
                hideAlert(alertIdRef.current);
                alertIdRef.current = null;
            }

            let message = null;
            let title = null;

            // Weekend banner: Friday 5pm CT - Sunday 4:45pm CT
            if (day === 0 && time < 1645) {
                // Sunday before 4:45pm CT
                message = weekendMessage;
                title = 'Weekend Hours';
            } else if (day === 6) {
                // Saturday all day
                message = weekendMessage;
                title = 'Weekend Hours';
            } else if (day === 5 && time >= 1700) {
                // Friday after 5pm CT
                message = weekendMessage;
                title = 'Weekend Hours';
            } else if (day >= 1 && day <= 4 && time >= 1600 && time < 1645) {
                // Monday-Thursday between 4pm CT - 4:45pm CT
                message = marketCloseMessage;
                title = 'Market Closed';
            }

            if (message && title) {
                const id = showAlert({
                    type: 'warning',
                    title,
                    message,
                    dismissible: true,
                    autoHide: false
                });
                alertIdRef.current = id;
            }
        };

        checkMarketStatus();

        const interval = setInterval(checkMarketStatus, 60000);

        return () => {
            clearInterval(interval);
            if (alertIdRef.current) {
                hideAlert(alertIdRef.current);
                alertIdRef.current = null;
            }
        };
    }, [enabled, weekendMessage, marketCloseMessage, showAlert, hideAlert]);
};